import React, { useCallback, useMemo, useContext } from "react";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

import { FieldOption } from "components/FieldSelect/FieldSelect";
import ModalFieldSelect from "components/ModalFieldSelect";
import ModalFieldText from "components/ModalFieldText";
import { OnCompleteOutput } from "components/ModalWizard/ModalWizard";
import EditableTable from "components/EditableTable";
import { getCurrencyOptions } from "services/getCurrencyOptions";
import { getSessionDurationOptions } from "services/getSessionDurationOptions";
import { ValidationCallback } from "validation/validate";
import { useStickySort } from "hooks/useStickySort";
import { useSessionTypes } from "hooks/useSessionTypes";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import { DashboardContext } from "components/PageDashboard/PageDashboard";
import StepSessionType from "components/PageDashboard/StepSessionType";
import StepSessionDuration from "components/PageDashboard/StepSessionDuration";
import StepGuestUid from "components/PageDashboard/StepGuestUid";
import StepPrice from "components/PageDashboard/StepPrice";
import terminology from "terminology.json";
import { ucFirst } from "utils/ucFirst";
import FieldMenteeUid from "components/EditableTable/Fields/FieldMenteeUid/index";
import { EditableTableColumn } from "components/EditableTable/EditableTable";
import { withSelectedOption } from "services/withSelectedOption";
import { Params as SubmitEditableTableChangesParams } from "api/makeApiRequestType/makeSubmitEditableTableChanges";
import { ModalWizardStep } from "components/ModalWizard/ModalWizard";
import { columnDefinitionIsEnabled } from "components/EditableTable/columnDefinitionIsEnabled";

type OnEdit<T> = (
	arg: SubmitEditableTableChangesParams<T>["rows"]
) => OnCompleteOutput;

type Props<T> = {
	pricesWithSessionTypes: T[];
	isLoading: boolean;
	isWaiting: boolean;
	isError: boolean;
	waitModalTitle: string;
	waitModalIsShowing: boolean;
	onEdit: OnEdit<T>;
	extraValidation?: ValidationCallback<T>;
	extraColumns?: {
		column: EditableTableColumn<T>;
		after: string;
	}[];
	extraNewRowWizardSteps?: { step: ModalWizardStep<T>; after: string }[];
	newRowWizardStepTitles?: { [k: string]: string };
};

function PricesTable<
	T extends {
		id: string;
		series?: string;
		sessionTypeId: string;
		sessionTitle: string;
		price: number;
		currency: string;
		isEnabled: boolean;
		guestUid: string | null;
		sessionDurationMinutes: number;
	}
>({
	pricesWithSessionTypes,
	isLoading,
	isWaiting,
	isError,
	onEdit: onEditGlobal,
	waitModalTitle,
	waitModalIsShowing,
	extraValidation,
	extraColumns,
	extraNewRowWizardSteps,
	newRowWizardStepTitles
}: Props<T>) {
	const [{ isSignedIn, uid }] = useContext(AuthContext);

	const [dashboardContextState] = useContext(DashboardContext);

	const onNewRowWizardComplete = useCallback(
		(data: T) => {
			return onEditGlobal([
				{
					new: {
						...data,
						isEnabled: true
					}
				}
			]);
		},
		[onEditGlobal]
	);

	const sortedPrices = useStickySort(
		pricesWithSessionTypes,
		useCallback((a: T, b: T) => {
			const titleComparison = a.sessionTitle.localeCompare(b.sessionTitle);
			if (titleComparison === 0) {
				return a.price - b.price;
			}
			return titleComparison;
		}, []),
		"series"
	);

	const sessionTypes = useSessionTypes({
		mentorId: uid,
		showDisabled: true,
		doWait: isSignedIn === undefined || !uid,
		repeatNumber: dashboardContextState.sessionTypesRefreshNum,
		persistBetweenLoads: true
	});

	const rows = useMemo(
		() =>
			sortedPrices.map(data => ({
				key: data.id,
				data,
				actionWhenEditing: {
					icon: faTrash,
					label: "Delete",
					onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
						e.preventDefault();
						onEditGlobal([{ old: data }]);
					}
				}
			})),
		[sortedPrices, onEditGlobal]
	);

	const defaultNewRowSteps = [
		{
			key: "sessionTypeId",
			title: "What session type does this apply to?",
			shortTitle: "Session type",
			component: StepSessionType,
			doScrollToInitialPosition: true
		},
		{
			key: "price",
			title: "How much does this session cost?",
			shortTitle: "Price",
			component: StepPrice
		},
		{
			key: "session-duration",
			title: "How long does this session last?",
			shortTitle: "Duration",
			component: StepSessionDuration
		},
		{
			key: "guestUid",
			title: `Is this price reserved for a particular ${terminology.mentee}?`,
			shortTitle: ucFirst(terminology.mentee),
			component: StepGuestUid
		}
	].map(item => {
		if (!newRowWizardStepTitles) {
			return item;
		}
		if (!newRowWizardStepTitles[item.key]) {
			return item;
		}
		return {
			...item,
			title: newRowWizardStepTitles[item.key]
		};
	});

	// TODO:WV:20250313:Memoise
	const newRowWizardSteps: ModalWizardStep<
		T
	>[] = (defaultNewRowSteps as unknown) as ModalWizardStep<T>[];
	if (extraNewRowWizardSteps) {
		for (const { step, after } of extraNewRowWizardSteps) {
			const index = defaultNewRowSteps.findIndex(row => row.key === after);
			newRowWizardSteps.splice(index + 1, 0, step);
		}
	}

	// It is necessary to wrap this function in useCallback
	// to prevent multiple form submissions after the ModalWizard
	// completes.
	// TODO:WV:20250325:Write an integration test for this
	const validateNewRowData: ValidationCallback<T> = useCallback(
		(data, utils, auth) => {
			const { checkField } = utils;
			checkField("sessionTypeId", { type: "string" });
			checkField("price", { type: "number" });
			checkField("currency", { type: "string" });
			if (extraValidation) {
				extraValidation(data, utils, auth);
			}
		},
		[extraValidation]
	);

	const newRowWizard = useMemo(() => {
		const steps = newRowWizardSteps;
		return {
			steps,
			validateNewRowData,
			initialData: {}
		};
	}, [newRowWizardSteps, validateNewRowData]);

	const onOKSimpleSelectField = useCallback(
		(
			newOptions: FieldOption[],
			row: T,
			rowKey: keyof T,
			onEdit: typeof onEditGlobal
		) => {
			withSelectedOption(newOptions, value => {
				const newData = {
					...row,
					[rowKey]: value
				};
				onEdit([{ old: row, new: newData }]);
			});
		},
		[]
	);

	const defaultCols: EditableTableColumn<T>[] = [
		{
			key: "sessionTypeId",
			heading: "Session type",
			field: ({ row, rowKey, isEditing, onEdit }) => (
				<ModalFieldSelect
					isUnlocked={isEditing}
					title="Session type"
					requireSelection
					onOK={newOptions => {
						onOKSimpleSelectField(newOptions, row, rowKey, onEdit);
					}}
					options={
						sessionTypes.output
							? sessionTypes.output.page.map(({ id, sessionTitle }) => ({
									id,
									value: id,
									text: sessionTitle,
									selected: id === row.sessionTypeId
							  }))
							: []
					}
					allowMultipleSelections={false}
				/>
			)
		},
		{
			key: "currency",
			heading: "Currency",
			field: ({ row, rowKey, isEditing, onEdit }) => (
				<ModalFieldSelect
					isUnlocked={isEditing}
					title="Currency"
					requireSelection
					onOK={newOptions => {
						onOKSimpleSelectField(newOptions, row, rowKey, onEdit);
					}}
					options={getCurrencyOptions(row.currency)}
					allowMultipleSelections={false}
				/>
			)
		},
		{
			/* Number fields are rubbish.  Replace with a suitable field, or use a text with proper validation. */
			key: "price",
			heading: "Price",
			field: ({ row, rowKey, isEditing, onEdit }) => (
				<ModalFieldText
					isUnlocked={isEditing}
					title="Price"
					subType="number"
					onOK={newValue => {
						const newData = {
							...row,
							[rowKey]: newValue ? parseFloat(newValue) : 0
						};
						onEdit([{ old: row, new: newData }]);
					}}
					value={row.price + ""}
					formatValue={v => (v === "0" ? "FREE" : v)}
					helptext="For a free session, set the price to 0"
				/>
			)
		},
		{
			key: "sessionDurationMinutes",
			heading: "Duration",
			field: ({ row, rowKey, isEditing, onEdit }) => (
				<ModalFieldSelect
					isUnlocked={isEditing}
					title="Session duration"
					requireSelection
					onOK={newOptions => {
						withSelectedOption(newOptions, value => {
							const newData = {
								...row,
								[rowKey]: parseInt(value + "")
							};
							onEdit([{ old: row, new: newData }]);
						});
					}}
					options={getSessionDurationOptions(row.sessionDurationMinutes)}
					allowMultipleSelections={false}
				/>
			)
		},
		{
			key: "guestUid",
			heading: ucFirst(terminology.mentee),
			field: ({ row, rowKey, isEditing, onEdit }) => (
				<FieldMenteeUid
					row={row}
					rowKey={rowKey}
					isEditing={isEditing}
					onEdit={onEdit}
				/>
			)
		},
		columnDefinitionIsEnabled({ initialValue: row => !!row.isEnabled })
	];

	const cols: EditableTableColumn<T>[] = defaultCols;
	if (extraColumns) {
		for (const { column, after } of extraColumns) {
			const index = defaultCols.findIndex(row => row.key === after);
			cols.splice(index + 1, 0, column);
		}
	}

	return (
		<>
			<EditableTable<T>
				allowDeleteRows
				rows={rows}
				onEdit={onEditGlobal}
				dataDescriptionSingular="session price"
				dataDescriptionPlural="session prices"
				onNew={onNewRowWizardComplete}
				newRowWizard={newRowWizard}
				rowIsDisabled={row => !row.isEnabled}
				isLoading={isLoading || sessionTypes.isLoading}
				isWaiting={isWaiting || sessionTypes.isWaiting}
				isError={isError || sessionTypes.isError}
				waitModalTitle={waitModalTitle}
				waitModalIsShowing={waitModalIsShowing}
				cols={cols}
			/>
		</>
	);
}

export default PricesTable;
